import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import NewsletterForm from '../NewsletterForm/NewsletterForm'; // Import du composant newsletter
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Instagram,
  Globe,
  ExternalLink,
  ChevronRight,
  User,
  UserCog
} from 'lucide-react';

// Icône TikTok personnalisée
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const socialLinks = [
    { icon: <Facebook size={20} />, href: "https://www.facebook.com/Cfaestloiret/", color: "hover:bg-blue-600" },
    { icon: <Instagram size={20} />, href: "https://www.instagram.com/cfaestloiret/", color: "hover:bg-pink-600" },
    { icon: <TikTokIcon size={20} />, href: "https://www.tiktok.com/@cfa.est.loiret", color: "hover:bg-black" },
  ];

  const usefulLinks = [
    { name: "Mentions légales", to: "/legal/mentions" },
    { name: "Politique de confidentialité", to: "/legal/rgpd" },
    { name: "Conditions d'utilisation", to: "/legal/conditions" },
  ];

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
      className="bg-slate-900/80 backdrop-blur-md mt-auto border-t border-slate-800/60 relative z-10"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90 -z-10"></div>
      
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <motion.h3 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="text-xl font-semibold mb-6 text-white relative inline-block"
            >
              Contact
              <motion.span 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 1, duration: 0.8 }}
                className="absolute bottom-0 left-0 h-0.5 bg-blue-500"
              ></motion.span>
            </motion.h3>
            
            <ul className="space-y-4">
              <motion.li 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
                className="flex items-start text-gray-300 group"
              >
                <div className="mt-1 mr-3 p-2 bg-blue-900/30 rounded-full text-blue-400 group-hover:text-blue-300 transition-colors">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="leading-relaxed">3 rue Cyrille Robert<br />45200 Montargis</p>
                </div>
              </motion.li>
              
              <motion.li 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 }}
                className="flex items-center text-gray-300 group"
              >
                <div className="mr-3 p-2 bg-blue-900/30 rounded-full text-blue-400 group-hover:text-blue-300 transition-colors">
                  <Phone size={18} />
                </div>
                <p>02 38 98 05 92</p>
              </motion.li>
              
              <motion.li 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
                className="flex items-center text-gray-300 group"
              >
                <div className="mr-3 p-2 bg-blue-900/30 rounded-full text-blue-400 group-hover:text-blue-300 transition-colors">
                  <Mail size={18} />
                </div>
                <a href="mailto:accueil@cfa-montargis.com" className="hover:text-blue-400 transition-colors">
                  accueil@cfa-montargis.com
                </a>
              </motion.li>
              
              <motion.li 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
                className="flex items-center text-gray-300 group"
              >
                <div className="mr-3 p-2 bg-blue-900/30 rounded-full text-blue-400 group-hover:text-blue-300 transition-colors">
                  <Globe size={18} />
                </div>
                <a href="https://www.cfa-montargis.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors flex items-center">
                  www.cfa-montargis.com
                  <ExternalLink size={14} className="ml-1" />
                </a>
              </motion.li>
            </ul>

            {/* Section Direction */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              className="mt-8 pt-6 border-t border-slate-700/60"
            >
              <ul className="space-y-3">
                <motion.li 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.4 }}
                  className="flex items-center text-gray-300 group"
                >
                  <div className="mr-3 p-2 bg-purple-900/30 rounded-full text-purple-400 group-hover:text-purple-300 transition-colors">
                    <UserCog size={18} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Directeur</p>
                    <p className="font-medium">M. Fabrice GONTARD</p>
                  </div>
                </motion.li>
                
                <motion.li 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.5 }}
                  className="flex items-center text-gray-300 group"
                >
                  <div className="mr-3 p-2 bg-amber-900/30 rounded-full text-amber-400 group-hover:text-amber-300 transition-colors">
                    <User size={18} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Président</p>
                    <p className="font-medium">M. Philippe MOREAU</p>
                  </div>
                </motion.li>
              </ul>
            </motion.div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <motion.h3 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="text-xl font-semibold mb-6 text-white relative inline-block"
            >
              Liens utiles
              <motion.span 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 1.1, duration: 0.8 }}
                className="absolute bottom-0 left-0 h-0.5 bg-blue-500"
              ></motion.span>
            </motion.h3>
            
            <ul className="space-y-3">
              {usefulLinks.map((link, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + (index * 0.1) }}
                >
                  <Link 
                    to={link.to} 
                    className="text-gray-300 hover:text-blue-400 transition-colors flex items-center group"
                  >
                    <ChevronRight size={16} className="mr-2 text-blue-500 transform group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              className="mt-8 p-4 bg-blue-900/20 rounded-lg border border-blue-900/40"
            >
              <p className="text-gray-300 text-sm">
                Le CFA Est Loiret s'engage à fournir une expérience de formation de qualité, tout en respectant les normes d'accessibilité et de confidentialité.
              </p>
            </motion.div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <motion.h3 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
              className="text-xl font-semibold mb-6 text-white relative inline-block"
            >
              Suivez-nous
              <motion.span 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="absolute bottom-0 left-0 h-0.5 bg-blue-500"
              ></motion.span>
            </motion.h3>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="flex space-x-3 mb-6"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.1 + (index * 0.1) }}
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  className={`bg-slate-800 p-3 rounded-full text-gray-300 hover:text-white transition-colors ${social.color}`}
                >
                  {social.icon}
                </motion.a>
              ))}
            </motion.div>
            
            {/* Remplacé par le composant NewsletterForm */}
            <NewsletterForm />
          </motion.div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-12 pt-8 border-t border-slate-700/60 text-center text-gray-400"
        >
          <div className="flex flex-col md:flex-row items-center justify-between">
            <p className="text-sm mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} CFA Est Loiret. AUBRY Jean-Charles. Tous droits réservés.
            </p>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="flex space-x-4 text-xs"
            >
              <Link to="/legal/conditions" className="text-gray-400 hover:text-blue-400 transition-colors">CGU</Link>
              <span className="text-gray-600">|</span>
              <Link to="/legal/rgpd" className="text-gray-400 hover:text-blue-400 transition-colors">RGPD</Link>
              <span className="text-gray-600">|</span>
              <Link to="/legal/mentions" className="text-gray-400 hover:text-blue-400 transition-colors">Mentions légales</Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;